// components/sections/Header.js
import React, { useState, useEffect } from 'react';
import { Menu, X, User, LogOut, ChevronDown, ShoppingBag, Bell } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import axiosInstance from '../../api/axiosInstance';
import { notificationsAPI } from '../../api/notifications';

import logo from '../../public/images/Leikin_Team_Logo.png';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [programs, setPrograms] = useState([]);
  const [isProgramsDropdownOpen, setIsProgramsDropdownOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const { isAuthenticated, user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isAdminUser = Boolean(user?.is_staff || user?.is_superuser);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const fetchPrograms = async () => {
      try {
        const response = await axiosInstance.get('/trainings/');
        // Исключаем силовую тренировку – для неё есть отдельная страница /power-training
        const filtered = response.data.filter(p => p.slug !== 'power-training');
        setPrograms(filtered);
      } catch (err) {
        console.error('Error fetching programs:', err);
      }
    };
    fetchPrograms();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isProgramsDropdownOpen && !event.target.closest('.programs-dropdown')) {
        setIsProgramsDropdownOpen(false);
      }
      if (isNotificationsOpen && !event.target.closest('.notifications-dropdown')) {
        setIsNotificationsOpen(false);
      }
    };

    if (isProgramsDropdownOpen || isNotificationsOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [isProgramsDropdownOpen, isNotificationsOpen]);

  useEffect(() => {
    if (!isAuthenticated) {
      setNotifications([]);
      return;
    }

    const loadNotifications = async () => {
      try {
        const rawData = await notificationsAPI.getNotifications({ limit: 50 });
        const list = Array.isArray(rawData) ? rawData : (rawData?.results || []);
        setNotifications(list);
      } catch (error) {
        setNotifications([]);
      }
    };

    loadNotifications();
    const intervalId = window.setInterval(loadNotifications, 60000);
    return () => window.clearInterval(intervalId);
  }, [isAuthenticated]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Ошибка выхода:', error);
    }
  };

  const scrollToSection = (sectionId) => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          const headerOffset = 80;
          const elementRect = element.getBoundingClientRect();
          const absoluteElementTop = elementRect.top + window.scrollY;
          window.scrollTo({
            top: absoluteElementTop - headerOffset,
            behavior: 'smooth'
          });
        }
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        const headerOffset = 80;
        element.style.scrollMarginTop = headerOffset + 'px';
        element.scrollIntoView({ 
          behavior: 'smooth',
          block: 'start'
        });
      }
    }
    setIsMenuOpen(false);
  };

  // Основные пункты навигации по лендингу
  // Убираем "Силовая тренировка" из хэдера,
  // так как отдельная страница и раздел уже есть в блоке "Программы".
  const menuItems = [
    { id: 'about', label: 'О нас' },
  ];

  const pageItems = [
    { path: '/events', label: 'События' },
    { path: '/coaches', label: 'Тренеры' },
  ];

  const isMainPage = location.pathname === '/';
  const dashboardNotifications = [...notifications]
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    .slice(0, 50);
  const unreadNotificationsCount = dashboardNotifications.length;

  const markNotificationAsSeen = async (notificationId) => {
    if (!notificationId) return;
    await notificationsAPI.markSeen(notificationId);
    setNotifications((prev) => prev.filter((item) => item.id !== notificationId));
  };

  const clearNotifications = async () => {
    if (!dashboardNotifications.length) return;
    await notificationsAPI.clearAll();
    setNotifications([]);
  };

  const handleNotificationClick = async (item) => {
    await markNotificationAsSeen(item?.id);
    setIsNotificationsOpen(false);
    if (!item || item.action === 'deleted') return;

    if (item.target_type === 'event') {
      navigate('/events');
      return;
    }

    if (item.target_type === 'group_training') {
      navigate('/dashboard?tab=profile');
    }
  };

  const renderNotificationsDropdown = (className = '') => (
    <div className={`absolute top-full mt-3 w-[min(22rem,calc(100vw-1rem))] bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden z-50 ${className}`}>
      <div className="absolute -top-2 right-5 h-4 w-4 rotate-45 border-l border-t border-gray-200 bg-white" />
      <div className="relative bg-white">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-sm font-bold text-gray-900">Уведомления</h3>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              clearNotifications();
            }}
            className="text-xs font-medium text-gray-600 hover:text-gray-900"
          >
            Очистить
          </button>
        </div>
        <div className="max-h-96 overflow-y-auto divide-y">
          {dashboardNotifications.length > 0 ? (
            dashboardNotifications.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleNotificationClick(item);
                }}
                className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{item.title}</p>
                    <p className="text-xs text-gray-600 mt-1 whitespace-pre-line line-clamp-3">{item.message}</p>
                  </div>
                  <span className="mt-1 w-2 h-2 rounded-full bg-red-500 shrink-0" />
                </div>
                <p className="text-[11px] text-gray-500 mt-2">
                  {new Date(item.created_at).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                </p>
              </button>
            ))
          ) : (
            <div className="px-4 py-8 text-center text-sm text-gray-500">Пока нет уведомлений</div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <header
        className={`fixed w-full z-50 transition-all duration-300 ${
          isScrolled || !isMainPage || isMenuOpen
            ? 'bg-white/85 backdrop-blur-md shadow-lg'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-1">
            <Link to="/" className="cursor-pointer flex items-center">
              <img
                src={logo}
                alt="Leikin Team Logo"
                className="h-12 sm:h-16 md:h-20 w-auto transition-transform duration-300 hover:scale-105"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              {/* Main page sections (всегда показываем, скролл работает и с других страниц) */}
              {menuItems.map(item => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`font-medium transition-colors duration-300 ${
                    isScrolled || !isMainPage
                      ? 'text-gray-600 hover:text-blue-600'
                      : 'text-white/90 hover:text-white'
                  }`}
                >
                  {item.label}
                </button>
              ))}

              {/* Programs Dropdown */}
              <div 
                className="relative programs-dropdown"
              >
                <button
                  className={`flex items-center font-medium transition-colors duration-300 ${
                    isScrolled || !isMainPage
                      ? 'text-gray-600 hover:text-blue-600'
                      : 'text-white/90 hover:text-white'
                  } ${
                    location.pathname.startsWith('/program/') || location.pathname.startsWith('/power-training') || location.pathname === '/programs' ? 'text-blue-600' : ''
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsProgramsDropdownOpen(!isProgramsDropdownOpen);
                  }}
                >
                  Программы
                  <ChevronDown className={`w-4 h-4 ml-1 transition-transform ${isProgramsDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                {isProgramsDropdownOpen && (
                  <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-lg shadow-lg py-2 z-50">
                    <Link
                      to="/power-training"
                      className="block px-4 py-2 text-gray-700 hover:bg-gray-100 transition-colors"
                      onClick={() => setIsProgramsDropdownOpen(false)}
                    >
                      Силовая тренировка
                    </Link>
                    {programs.length > 0 ? (
                      programs.map(program => (
                        <Link
                          key={program.id}
                          to={`/program/${program.slug || program.id}`}
                          className="block px-4 py-2 text-gray-700 hover:bg-gray-100 transition-colors"
                          onClick={() => setIsProgramsDropdownOpen(false)}
                        >
                          {program.title}
                        </Link>
                      ))
                    ) : (
                      <div className="px-4 py-2 text-gray-500 text-sm">Загрузка...</div>
                    )}
                    <div className="border-t border-gray-200 mt-2 pt-2">
                      <Link
                        to="/programs"
                        className="block px-4 py-2 text-gray-700 hover:bg-gray-100 transition-colors"
                        onClick={() => setIsProgramsDropdownOpen(false)}
                      >
                        Все программы
                      </Link>
                    </div>
                  </div>
                )}
              </div>

              {/* Page links */}
              {pageItems.map(item => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`font-medium transition-colors duration-300 ${
                    isScrolled || !isMainPage
                      ? 'text-gray-600 hover:text-blue-600'
                      : 'text-white/90 hover:text-white'
                  } ${
                    location.pathname === item.path ? 'text-blue-600' : ''
                  }`}
                >
                  {item.label}
                </Link>
              ))}

              {/* Auth buttons */}
              {isAuthenticated ? (
                <div className="flex items-center space-x-4">
                  <Link
                    to="/shop"
                    className={`flex items-center space-x-2 font-medium transition-colors duration-300 ${
                      isScrolled || !isMainPage
                        ? 'text-gray-600 hover:text-blue-600'
                        : 'text-white/90 hover:text-white'
                    }`}
                  >
                    <ShoppingBag className="h-5 w-5" />
                    <span>Магазин</span>
                  </Link>
                  {isAdminUser && (
                    <Link
                      to="/dashboard?tab=admin"
                      className={`flex items-center space-x-2 font-medium transition-colors duration-300 ${
                        isScrolled || !isMainPage
                          ? 'text-gray-600 hover:text-blue-600'
                          : 'text-white/90 hover:text-white'
                      }`}
                    >
                      <span>Админ</span>
                    </Link>
                  )}
                  <Link
                    to="/dashboard?tab=profile"
                    className={`flex items-center space-x-2 font-medium transition-colors duration-300 ${
                      isScrolled || !isMainPage
                        ? 'text-gray-600 hover:text-blue-600'
                        : 'text-white/90 hover:text-white'
                    }`}
                  >
                    <User className="h-5 w-5" />
                    <span>{user?.first_name || 'Профиль'}</span>
                  </Link>
                  <div className="relative notifications-dropdown">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsNotificationsOpen((prev) => !prev);
                      }}
                      className={`relative pr-3 pt-1 transition-colors duration-300 ${
                        isScrolled || !isMainPage
                          ? 'text-gray-600 hover:text-blue-600'
                          : 'text-white/90 hover:text-white'
                      }`}
                      aria-label="Открыть уведомления"
                    >
                      <Bell className="h-5 w-5" />
                      {unreadNotificationsCount > 0 && (
                        <span className="absolute -top-1 right-0 translate-x-1/2 -translate-y-1/2 min-w-[18px] h-[18px] rounded-full bg-red-500 px-1 text-[11px] leading-[18px] text-white font-semibold text-center z-10">
                          {unreadNotificationsCount > 99 ? '99+' : unreadNotificationsCount}
                        </span>
                      )}
                    </button>
                    {isNotificationsOpen && renderNotificationsDropdown('right-0 hidden md:block')}
                  </div>
                  <button
                    onClick={handleLogout}
                    className={`flex items-center space-x-2 font-medium transition-colors duration-300 ${
                      isScrolled || !isMainPage
                        ? 'text-gray-600 hover:text-red-600'
                        : 'text-white/90 hover:text-red-300'
                    }`}
                  >
                    <LogOut className="h-5 w-5" />
                    <span>Выйти</span>
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => navigate('/auth')}
                  className="transform hover:scale-105 bg-gradient-to-r from-custom-purple via-custom-blue to-custom-light-blue text-white font-semibold px-6 py-3 rounded-lg transition-all duration-300"
              >
                Вход
                </button>
              )}
            </nav>

            {/* Mobile Menu Button */}
            <div className="md:hidden flex items-center gap-3">
              {isAuthenticated && (
                <div className="relative notifications-dropdown">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsNotificationsOpen((prev) => !prev);
                    }}
                    className={`relative pr-3 pt-1 transition-colors duration-300 ${
                      isScrolled || !isMainPage || isMenuOpen
                        ? 'text-gray-600'
                        : 'text-white'
                    }`}
                    aria-label="Открыть уведомления"
                  >
                    <Bell size={22} />
                    {unreadNotificationsCount > 0 && (
                      <span className="absolute -top-1 right-0 translate-x-1/2 -translate-y-1/2 min-w-[18px] h-[18px] rounded-full bg-red-500 px-1 text-[11px] leading-[18px] text-white font-semibold text-center z-10">
                        {unreadNotificationsCount > 99 ? '99+' : unreadNotificationsCount}
                      </span>
                    )}
                  </button>
                  {isNotificationsOpen && renderNotificationsDropdown('right-0 md:hidden')}
                </div>
              )}
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className={`transition-colors duration-300 z-50 ${
                  isScrolled || !isMainPage || isMenuOpen
                    ? 'text-gray-600'
                    : 'text-white'
                }`}
              >
                {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {isMenuOpen && (
            <div className="md:hidden fixed inset-x-0 top-[72px] h-[calc(100vh-72px)] bg-white/95 backdrop-blur-md shadow-lg py-4 overflow-y-auto z-40">
              <nav className="flex flex-col space-y-4 px-4 pb-6">
                {/* Main page sections */}
                {isMainPage && menuItems.map(item => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className="text-gray-600 hover:text-blue-600 py-2 text-left"
                  >
                    {item.label}
                  </button>
                ))}

                {/* Programs Section */}
                <div className="border-t border-gray-200 pt-4">
                  <div className="text-gray-900 font-semibold mb-2">Программы</div>
                  <Link
                    to="/power-training"
                    className={`block text-gray-600 hover:text-blue-600 py-2 pl-4 ${
                      location.pathname === '/power-training' ? 'text-blue-600' : ''
                    }`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Силовая тренировка
                  </Link>
                  {programs.length > 0 ? (
                    programs.map(program => (
                      <Link
                        key={program.id}
                        to={`/program/${program.slug || program.id}`}
                        className={`block text-gray-600 hover:text-blue-600 py-2 pl-4 ${
                          location.pathname === `/program/${program.slug || program.id}` ? 'text-blue-600' : ''
                        }`}
                        onClick={() => setIsMenuOpen(false)}
                      >
                        {program.title}
                      </Link>
                    ))
                  ) : (
                    <div className="text-gray-500 text-sm py-2 pl-4">Загрузка...</div>
                  )}
                  <Link
                    to="/programs"
                    className="block text-gray-600 hover:text-blue-600 py-2 pl-4"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Все программы
                  </Link>
                </div>

                {/* Page links */}
                {pageItems.map(item => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`text-gray-600 hover:text-blue-600 py-2 text-left ${
                      location.pathname === item.path ? 'text-blue-600' : ''
                    }`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.label}
                  </Link>
                ))}

                {/* Auth buttons */}
                {isAuthenticated ? (
                  <>
                    <Link
                      to="/shop"
                      className="flex items-center space-x-2 text-gray-600 hover:text-blue-600 py-2"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <ShoppingBag className="h-5 w-5" />
                      <span>Магазин команды</span>
                    </Link>
                    {isAdminUser && (
                      <Link
                        to="/dashboard?tab=admin"
                        className="flex items-center space-x-2 text-gray-600 hover:text-blue-600 py-2"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        <span>Админ</span>
                      </Link>
                    )}
                    <Link
                      to="/dashboard?tab=profile"
                      className="flex items-center space-x-2 text-gray-600 hover:text-blue-600 py-2"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <User className="h-5 w-5" />
                      <span>{user?.first_name || 'Профиль'}</span>
                    </Link>
                    <button
                      onClick={() => {
                        handleLogout();
                        setIsMenuOpen(false);
                      }}
                      className="flex items-center space-x-2 text-gray-600 hover:text-red-600 py-2 w-full text-left"
                    >
                      <LogOut className="h-5 w-5" />
                      <span>Выйти</span>
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => navigate('/auth')}
                    className="transform hover:scale-105 bg-gradient-to-r from-custom-purple via-custom-blue to-custom-light-blue text-white font-semibold px-6 py-3 rounded-lg transition-all duration-300"
                >
                  Вход
                  </button>
                )}
              </nav>
            </div>
          )}
        </div>
      </header>
    </>
  );
};

export default Header;
